/**
 * Applying the server's ladder trim so that failing to apply it is visible.
 *
 * The server tells a publisher the highest rung anyone still wants. Obeying it
 * used to be one line — `producer.setMaxSpatialLayer(n)` — and that line cannot
 * fail loudly enough to be useful. In mediasoup-client 3.20:
 *
 *   await new Promise((resolve, reject) => {
 *     this.safeEmit('@setmaxspatiallayer', spatialLayer, resolve, reject);
 *   }).catch(() => { });          // the failure is thrown away
 *   this._maxSpatialLayer = spatialLayer;   // and recorded as applied anyway
 *
 * So a refused renegotiation left the rungs switched off while both ends
 * believed they were on. Worse, the cached `_maxSpatialLayer` then makes every
 * later request for the same value an early return, and the server (before its
 * own fix) never restated it — one lost transition was permanent, for the life
 * of the call. Publishers ran whole meetings with r1/r2 dark, and every viewer
 * who needed a rung above the latch sat frozen on a perfect network.
 *
 * Two changes fix that here:
 *
 *  1. We write the `active` flags ourselves, through the ladder, and then READ
 *     THE SENDER BACK. A `setParameters` that resolves without taking effect is
 *     reported as `ignored` rather than success.
 *  2. The trim and the ladder have ONE writer. `applyLadderToSender` already
 *     owned `maxBitrate`/`scaleResolutionDownBy` and also wrote `active`; the
 *     trim wrote `active` too, from a different code path, with no ordering
 *     between them. Both now go through the same call, so a camera switch can
 *     never resurrect a trimmed rung and a trim can never resurrect a rung the
 *     ladder retired.
 *
 * Toggling `active` needs no renegotiation — it is a plain `setParameters` on a
 * live sender, the same mechanism the ladder reapply has always used. That is
 * the other reason not to route this through mediasoup: its handler does
 * `setParameters` AND a full offer/answer cycle on a send transport shared with
 * the microphone and the screen share, so a burst of layer changes was a burst
 * of renegotiations, which is what made them collide and fail in the first place.
 */
import { applyLadderToSender, type SenderEncoding } from './producer-encodings'
import type { LadderEncoding } from './encoding-ladder'

export type LayerSender = {
  getParameters(): { encodings?: SenderEncoding[] }
  setParameters(p: never): Promise<void>
}

/** What `active` should read on each rung, given the ladder and the trim. */
export function expectedActive(
  rungCount: number,
  ladderLength: number,
  maxSpatialLayer: number,
): boolean[] {
  return Array.from({ length: rungCount }, (_, i) => i < ladderLength && i <= maxSpatialLayer)
}

/**
 * Does the sender's live state already match what we asked for?
 *
 * `active` is optional in the spec and defaults to true, so an encoding that
 * has never been touched reads as on.
 */
export function capApplied(
  encodings: readonly SenderEncoding[] | undefined,
  ladderLength: number,
  maxSpatialLayer: number,
): boolean {
  if (!encodings || encodings.length === 0) return false
  const want = expectedActive(encodings.length, ladderLength, maxSpatialLayer)
  return encodings.every((e, i) => (e.active ?? true) === want[i])
}

export type ApplyResult =
  /** Written and verified on the sender. */
  | 'applied'
  /** Already in the requested state; nothing to do. */
  | 'unchanged'
  /** No sender, or a sender with no encodings — nothing to write to yet. */
  | 'unavailable'
  /** `setParameters` threw. A lost race, usually. */
  | 'refused'
  /** The write resolved and the sender did not change. The silent failure. */
  | 'ignored'

/**
 * One attempt: write the ladder at this trim, then confirm from the sender.
 */
export async function applyCap(
  sender: LayerSender | null | undefined,
  ladder: readonly LadderEncoding[],
  maxSpatialLayer: number,
): Promise<ApplyResult> {
  if (!sender) return 'unavailable'

  const result = await applyLadderToSender(sender, ladder, maxSpatialLayer)
  if (result === 'unavailable' || result === 'refused') return result

  // The point of the exercise. Anything that reports success without changing
  // the wire is the bug this module exists to surface.
  let readback: SenderEncoding[] | undefined
  try {
    readback = sender.getParameters().encodings
  } catch {
    return 'refused'
  }
  if (!capApplied(readback, ladder.length, maxSpatialLayer)) return 'ignored'
  return result
}

export type RetryOptions = {
  /** How many times to try again after a non-terminal failure. */
  retries?: number
  /** Backoff before attempt n, in ms. */
  delayMs?: number
  /** Injectable for tests. */
  wait?: (ms: number) => Promise<void>
  /** Bail out early — e.g. the request was superseded or the producer closed. */
  isStale?: () => boolean
  onAttempt?: (info: { attempt: number; result: ApplyResult; maxSpatialLayer: number }) => void
}

const realWait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * Apply the trim, retrying the failures that are worth retrying.
 *
 * `unavailable` is not one of them: there is no producer to write to right now,
 * and the server re-asserts its wish on an interval, so the next push will find
 * one. Retrying here would just spin.
 *
 * The sender is re-read on every attempt rather than captured once, because the
 * producer can be replaced underneath us — a camera switch or a virtual
 * background toggle both do it — and writing to the retired sender would
 * "succeed" against something nobody is sending.
 */
export async function applyCapWithRetry(
  getSender: () => LayerSender | null | undefined,
  getLadder: () => readonly LadderEncoding[],
  maxSpatialLayer: number,
  opts: RetryOptions = {},
): Promise<ApplyResult> {
  const retries = opts.retries ?? 3
  const delayMs = opts.delayMs ?? 400
  const wait = opts.wait ?? realWait

  let last: ApplyResult = 'unavailable'
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (opts.isStale?.()) return last
    last = await applyCap(getSender(), getLadder(), maxSpatialLayer)
    opts.onAttempt?.({ attempt, result: last, maxSpatialLayer })

    if (last === 'applied' || last === 'unchanged' || last === 'unavailable') return last
    if (attempt < retries) await wait(delayMs * (attempt + 1))
  }
  return last
}
